import * as React from 'react';
import { ArrowLongRightIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';

import { cn } from '@restworld/utils-common';
import { focusRing, Logo } from '@restworld/ui-ds-base';

import {
  buildVenueInternalLinks,
  venueInternalLinkHref,
  VenueInternalLinksSource,
} from '../../lib/seo/venue-internal-links';

interface IVenueFooterProps {
  /**
   * Comes from the `getStaticProps` payload, not from `restaurantDetailsAtom`:
   * the atom is only populated client-side, and a link Google cannot see in the
   * served markup is the defect this block exists to close.
   */
  venueLocation?: VenueInternalLinksSource | null;
}

/**
 * The two sides of Restworld, as links and not as buttons.
 *
 * These used to be `ButtonV2` with a `window.open` handler, so the served markup
 * carried no `<a href>` for either: for a crawler the showcase pointed at nothing
 * on the worker side and nothing on the business side, which is the defect the
 * whole footer block exists to close. They are anchors now, styled to look
 * exactly as before.
 *
 * `/aziende` and not the bare homepage: someone who wants to hire should land on
 * the employer page, and the old link went to the root.
 *
 * `path`, not a full URL: `venueInternalLinkHref` adds the origin and the locale
 * prefix, so `/en/locale/x` links to `/en/aziende` and not to the Italian page.
 */
const FOOTER_ACTIONS = [
  { path: '/cerco-lavoro', labelKey: 'looking_for_a_job' },
  { path: '/aziende', labelKey: 'looking_for_a_talent' },
];

/**
 * The anchor that replaces `ButtonV2 variant="secondary-restworld" size="md"`.
 *
 * The values are lifted from that component rather than eyeballed: `text-purple-700
 * bg-purple-500/10` is its variant map, `h-12 px-4 text-base` its `md` size, and
 * `rounded-lg py-2 gap-2 font-semibold transition duration-200` its base. The
 * focus ring is the same one, imported from `ui-ds-base`, so keyboard users see
 * what they saw before.
 *
 * One thing is an approximation and not a copy: `ButtonV2` darkens through an
 * absolutely positioned overlay span at 15% and 20% opacity, which needs a second
 * element. Two background steps get close enough on a flat tinted surface, and an
 * extra span inside every footer link is not worth exactness nobody can see.
 */
const FooterAction = ({ className, ...props }: React.AnchorHTMLAttributes<HTMLAnchorElement>) => (
  <a
    className={cn(
      'w-full h-12 flex gap-2 items-center justify-center rounded-lg py-2 px-4',
      'text-base font-semibold text-purple-700 bg-purple-500/10',
      'transition duration-200 ease-in-out hover:bg-purple-500/20 active:bg-purple-500/25',
      focusRing,
      className
    )}
    {...props}
  />
);

const NAV_TITLE_ID = 'venue-footer-nav-title';

const VenueFooter: React.FC<IVenueFooterProps> = ({ venueLocation }) => {
  const { t } = useTranslation('restaurant-showcase-v2');
  // The legal line lives in `common` next to footer.all_right_reserved_text so the
  // two wordings of the same sentence sit side by side and can be consolidated.
  const { t: tCommon } = useTranslation('common');
  const { locale } = useRouter();

  // Every outgoing destination in this footer is a real `<a href>` in the served
  // markup, the two above and the city-aware ones here.
  const internalLinks = React.useMemo(() => buildVenueInternalLinks(venueLocation), [venueLocation]);

  return (
    <div className="w-full bg-purple-50 pb-18.25 md:pb-8 self-end">
      <div className="px-4 py-[5%] max-w-[640px] mx-auto">
        <div className="w-full flex flex-col justify-start items-center gap-8">
          {/* The empty path, not '/': the marketing site answers `/en/` with a 308.
              The width sits on the logo and not on the anchor: "Pagina offerta da
              Restworld" is two and a half times the "Powered by" it replaces, and
              inside the old 96px box it broke onto three lines. */}
          <a
            href={venueInternalLinkHref('', locale)}
            className="flex flex-col justify-start items-center gap-2 text-purple-700"
          >
            <span className="text-xs font-normal text-center">{t('page_offered_by')}</span>
            <Logo variant="horizontal" title="Restworld" className="w-24" />
          </a>
          <div className="self-stretch flex flex-col justify-center items-center gap-4">
            {FOOTER_ACTIONS.map(({ path, labelKey }) => (
              <FooterAction key={path} href={venueInternalLinkHref(path, locale)}>
                {t(labelKey)}
                <ArrowLongRightIcon className="w-5 h-5" aria-hidden="true" />
              </FooterAction>
            ))}
          </div>
          <nav aria-labelledby={NAV_TITLE_ID} className="self-stretch flex flex-col justify-start items-center gap-3">
            <h5 id={NAV_TITLE_ID} className="text-purple-700 text-sm font-semibold text-center">
              {t('explore_jobs')}
            </h5>
            <ul className="flex flex-col justify-start items-center gap-2">
              {internalLinks.map(({ path, labelKey, cityNameKey }) => (
                <li key={path}>
                  <a
                    href={venueInternalLinkHref(path, locale)}
                    className="text-purple-700 text-sm font-normal underline text-center"
                  >
                    {t(labelKey, { city: cityNameKey ? t(cityNameKey) : undefined })}
                  </a>
                </li>
              ))}
            </ul>
          </nav>
          <h5 className="justify-center text-neutral-700 text-sm font-normal">
            © Copyright {new Date().getFullYear()} {tCommon('footer.venue_showcase_reserved_text')}
          </h5>
        </div>
      </div>
    </div>
  );
};

export default VenueFooter;
